import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import LandingCoach from "@/components/LandingCoach";
import { captureRef } from "@/lib/referral";
import Logo from "@/components/Logo";
import { ArrowRight, MessageCircle, Eye, Repeat, Sparkles } from "lucide-react";

const loop = [
  { icon: MessageCircle, t: "Talk & Practise", d: "Speak with Xcelio, then rehearse the real conversations that make you nervous." },
  { icon: Eye, t: "The Mirror", d: "See what happened, where your energy changed, and the pattern underneath." },
  { icon: Sparkles, t: "The Shift", d: "Xcelio creates a personal installation to change what's underneath the words." },
  { icon: Repeat, t: "Real Life", d: "A small mission, back into the world — then return, reflect, and go deeper." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user) navigate("/app"); }, [user, navigate]);
  useEffect(() => { captureRef(); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground relative grain overflow-hidden">
      <header className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between relative z-10">
        <div className="flex items-center">
          <Logo className="text-2xl" showDot />
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login"><Button variant="ghost" className="rounded-full" data-testid="landing-login-btn">Log in</Button></Link>
          <Link to="/register"><Button className="rounded-full" data-testid="landing-signup-btn">Begin</Button></Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-28 grid lg:grid-cols-2 gap-16 items-center relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
          <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-primary mb-8">
            <span className="w-8 h-px bg-primary" /> Social intelligence coach
          </div>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-8">
            Don't become<br />more impressive.<br /><span className="italic text-primary">Become more you.</span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-md mb-10 leading-relaxed">
            Xcelio is an AI coach for connection, confidence and conversation. It doesn't just teach you what to say — it helps you change who you're being underneath.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register">
              <Button size="lg" className="rounded-full group" data-testid="hero-cta">
                Meet your Coach
                <ArrowRight className="w-4 h-4 ml-1 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2 }} className="flex justify-center">
          <div className="relative flex flex-col items-center justify-center">
            <LandingCoach />
            <p className="font-serif text-2xl sm:text-3xl italic text-foreground/80 text-center leading-snug mt-8">Talk. Understand. Evolve.</p>
          </div>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-28 relative z-10">
        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-8">The loop</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {loop.map((p, i) => (
            <motion.div key={p.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="rounded-2xl border border-border p-7 bg-card/50 hover:-translate-y-1 transition-transform duration-300">
              <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center mb-5"><p.icon className="w-5 h-5 text-primary" strokeWidth={1.5} /></div>
              <h3 className="font-serif text-2xl mb-2">{p.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground relative z-10">
        Xcelio — become more yourself.
      </footer>
    </div>
  );
}
